import React, { useMemo, useState, useEffect } from 'react';
import Select from 'react-select';
import ReactFileReader from 'react-file-reader';
import { useDispatch, useSelector } from 'react-redux';
import ModalDialog from '../../../core/components/Modal';
import { hideDialog } from '../../../core/actions';
import { selectSitesBasic } from '../../site-selection/reducer';
import { selectSite, selectJwt } from '../../../store/authReducer';
import { ControlledRichText } from '../../../core/formValidation';
import {
  shareWhatsNewUpdate,
  updateWhatsNewNotification,
  fetchNotifications as fetchNotificationsAction,
  markNotificationRead,
  WHATS_NEW_NOTIFICATION_TYPE,
} from '../actions';
import { parseNotificationBody, stripHtmlTags, sanitizeHtmlContent } from '../utils';
import { uploadNotificationMedia } from '../../../core/api/notifications';
import { TOKEN_HEADER } from '../../../core/constants';
import { useIntl } from 'react-intl';

interface WhatsNewUpdateDialogProps {
  isOpen: boolean;
  notification?: any;
}

type BasicSite = {
  id: number;
  name?: string;
  externalId?: string;
};

type NotificationMedia = {
  id?: number | string;
  fileName?: string;
  mimeType?: string;
  size?: number;
  downloadUrl?: string;
  url?: string;
  [key: string]: any;
};

const FILE_SIZE_LIMIT = 209715200; // 200 MB

const WhatsNewUpdateDialog = ({ isOpen, notification }: WhatsNewUpdateDialogProps) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const availableSites = (useSelector(selectSitesBasic) || []) as BasicSite[];
  const primarySiteId = useSelector(selectSite);
  const jwtToken = useSelector(selectJwt);
  const [audience, setAudience] = useState<'all' | 'specific'>('all');
  const [selectedSites, setSelectedSites] = useState<string[]>([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [initialDescription, setInitialDescription] = useState('');
  const [requireLoginRead, setRequireLoginRead] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [imageMedia, setImageMedia] = useState<NotificationMedia | null>(null);
  const [videoMedia, setVideoMedia] = useState<NotificationMedia | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [videoPreviewUrl, setVideoPreviewUrl] = useState('');
  const [legacyImagePreview, setLegacyImagePreview] = useState('');
  const [legacyVideoPreview, setLegacyVideoPreview] = useState('');
  const [mediaUploading, setMediaUploading] = useState<{ image: boolean; video: boolean }>({
    image: false,
    video: false,
  });
  const [mediaErrors, setMediaErrors] = useState<{ image?: string; video?: string }>({});

  const revokePreviewUrl = (url?: string) => {
    if (url && url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  };

  useEffect(() => {
    return () => {
      revokePreviewUrl(imagePreviewUrl);
      revokePreviewUrl(videoPreviewUrl);
    };
  }, [imagePreviewUrl, videoPreviewUrl]);

  const isEditing = Boolean(notification?.id);
  const closeModal = () => dispatch(hideDialog());

  useEffect(() => {
    if (!isOpen) {
      setAudience('all');
      setSelectedSites([]);
      setTitle('');
      setDescription('');
      setInitialDescription('');
      setRequireLoginRead(false);
      setLinkUrl('');
      setImageMedia(null);
      setVideoMedia(null);
      revokePreviewUrl(imagePreviewUrl);
      revokePreviewUrl(videoPreviewUrl);
      setImagePreviewUrl('');
      setVideoPreviewUrl('');
      setLegacyImagePreview('');
      setLegacyVideoPreview('');
      setMediaErrors({});
      setMediaUploading({ image: false, video: false });
      return;
    }

    if (notification) {
      setTitle(notification.title || '');
      const parsedBody = parseNotificationBody(notification.body);
      const parsedDescription = parsedBody.message || '';
      setDescription(parsedDescription);
      setInitialDescription(parsedDescription);
      setLegacyImagePreview(parsedBody.imageData || parsedBody.imageUrl || '');
      setLegacyVideoPreview(parsedBody.videoData || parsedBody.videoUrl || '');
      const mediaItems = Array.isArray(notification.media) ? notification.media : [];
      const existingImage = mediaItems.find(
        (entry: NotificationMedia) =>
          typeof entry?.mimeType === 'string' && entry.mimeType.startsWith('image')
      );
      const existingVideo = mediaItems.find(
        (entry: NotificationMedia) =>
          typeof entry?.mimeType === 'string' && entry.mimeType.startsWith('video')
      );
      setImageMedia(existingImage || null);
      setVideoMedia(existingVideo || null);
      revokePreviewUrl(imagePreviewUrl);
      revokePreviewUrl(videoPreviewUrl);
      setImagePreviewUrl('');
      setVideoPreviewUrl('');
      setLinkUrl(notification.link || parsedBody.linkUrl || '');
      setRequireLoginRead(Boolean(notification.showOnLogin));

      const specificSiteIds = Array.isArray(notification.siteIds)
        ? notification.siteIds
        : Array.isArray(notification.trackingRecords)
        ? Array.from(
            new Set(
              notification.trackingRecords
                .map((record: any) => record?.siteId)
                .filter((id: any) => Number.isFinite(Number(id)))
            )
          )
        : [];

      if (notification.sendToAllSites === false && specificSiteIds.length > 0) {
        setAudience('specific');
        setSelectedSites(specificSiteIds.map((id: any) => String(id)));
      } else if (notification.sendToAllSites === true) {
        setAudience('all');
        setSelectedSites([]);
      } else if (specificSiteIds.length > 0) {
        setAudience('specific');
        setSelectedSites(specificSiteIds.map((id: any) => String(id)));
      } else {
        setAudience('all');
        setSelectedSites([]);
      }
    } else {
      setAudience('all');
      setSelectedSites([]);
      setTitle('');
      setDescription('');
      setInitialDescription('');
      setRequireLoginRead(false);
      setLinkUrl('');
      setImageMedia(null);
      setVideoMedia(null);
      setLegacyImagePreview('');
      setLegacyVideoPreview('');
      setMediaErrors({});
      setMediaUploading({ image: false, video: false });
    }
  }, [isOpen, notification]);

  const handleAudienceChange = (value: 'all' | 'specific') => {
    setAudience(value);
    if (value === 'all') {
      setSelectedSites([]);
    }
  };

  const sanitizedDescriptionText = stripHtmlTags(description);

  const canSubmit =
    title.trim().length > 0 &&
    sanitizedDescriptionText.trim().length > 0 &&
    (audience === 'all' || selectedSites.length > 0);
  const isUploadingMedia = mediaUploading.image || mediaUploading.video;

  const handleSubmit = () => {
    if (!canSubmit || isUploadingMedia) return;

    const sendToAllSites = audience === 'all';
    const siteIds =
      !sendToAllSites && selectedSites.length
        ? selectedSites.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
        : undefined;

    const trimmedLinkUrl = linkUrl.trim();
    const mediaPayload = [imageMedia, videoMedia].filter((item): item is NotificationMedia =>
      Boolean(item)
    );

    const payload: any = {
      title: title.trim(),
      body: sanitizeHtmlContent(description.trim()),
      link: trimmedLinkUrl || null,
      type: WHATS_NEW_NOTIFICATION_TYPE,
      showOnLogin: requireLoginRead,
      sendToAllSites,
      ...(siteIds && siteIds.length ? { siteIds } : {}),
    };

    const refreshCallback = () =>
      fetchNotificationsAction({
        type: WHATS_NEW_NOTIFICATION_TYPE,
        includeTracking: true,
        ...(primarySiteId ? { siteId: primarySiteId } : {}),
      });

    const resetTrackingRecords = () => {
      if (!isEditing || !notification?.id) return;
      const trackingRecords = Array.isArray(notification.trackingRecords)
        ? notification.trackingRecords
        : [];

      trackingRecords
        .filter((record: any) => record && record.id && record.read === true)
        .forEach((record: any) => {
          dispatch(
            markNotificationRead({
              notificationId: notification.id,
              trackingId: record.id,
              read: false,
            })
          );
        });
    };

    payload.media = mediaPayload;

    if (isEditing && notification?.id) {
      const updatePayload = {
        id: notification.id,
        ...payload,
      };
      dispatch(
        updateWhatsNewNotification(updatePayload, {
          callback: () => {
            resetTrackingRecords();
            refreshCallback();
            window.dispatchEvent(new Event('whats-new-updated'));
          },
        })
      );
    } else {
      dispatch(
        shareWhatsNewUpdate(payload, {
          callback: () => {
            refreshCallback();
            window.dispatchEvent(new Event('whats-new-updated'));
          },
        })
      );
    }
  };

  const siteOptions = useMemo(() => {
    return availableSites.map((site: BasicSite) => ({
      value: site.id,
      label: site.externalId ? `${site.externalId} (${site.name})` : site.name || `Site ${site.id}`,
    }));
  }, [availableSites]);

  const selectedSiteOptions = useMemo(() => {
    const selectedSet = new Set(selectedSites);
    return siteOptions.filter((option) => selectedSet.has(String(option.value)));
  }, [siteOptions, selectedSites]);

  const handleSiteSelectChange = (options: any) => {
    if (!options || !Array.isArray(options)) {
      setSelectedSites([]);
      return;
    }
    setSelectedSites(options.map((option) => String(option.value)));
  };

  const sanitizeFileName = (name?: string) => {
    if (!name) {
      return 'upload';
    }
    return (
      name
        .replace(/[\r\n]+/g, '')
        .replace(/[^A-Za-z0-9._ -]+/g, '_')
        .trim()
        .substring(0, 200) || 'upload'
    );
  };

  const createMediaFormData = (file: File) => {
    const safeName = sanitizeFileName(file?.name);
    const formData = new FormData();
    formData.append('file', file, safeName);
    formData.append('description', safeName);
    return formData;
  };

  const uploadMediaFile = async (formData: FormData, mediaType: 'image' | 'video') => {
    if (!formData) return null;
    if (!jwtToken) {
      setMediaErrors((prev) => ({
        ...prev,
        [mediaType]: intl.formatMessage({
          id: 'authenticationRequiredToUploadMedia',
          defaultMessage: 'Authentication is required to upload media.',
        }),
      }));
      return null;
    }

    setMediaErrors((prev) => ({ ...prev, [mediaType]: '' }));
    setMediaUploading((prev) => ({ ...prev, [mediaType]: true }));

    try {
      const response = await uploadNotificationMedia({
        data: formData,
        options: {
          headers: {
            [TOKEN_HEADER]: jwtToken,
          },
        },
      });
      const metadata = response?.media || response;
      if (!metadata) {
        throw new Error('Missing media metadata');
      }
      return metadata;
    } catch (error) {
      setMediaErrors((prev) => ({
        ...prev,
        [mediaType]: intl.formatMessage({
          id: 'unableToUploadFile',
          defaultMessage: 'Unable to upload file. Please try again.',
        }),
      }));
      return null;
    } finally {
      setMediaUploading((prev) => ({ ...prev, [mediaType]: false }));
    }
  };

  const handleFiles = async (files: any, mediaType: 'image' | 'video') => {
    if (!files || !files.fileList || !files.fileList[0]) return;
    const file = files.fileList[0];

    // Validate file size
    if (file.size >= FILE_SIZE_LIMIT) {
      setMediaErrors((prev) => ({
        ...prev,
        [mediaType]: intl.formatMessage({ id: 'fileSizeError' }),
      }));
      return;
    }

    // Validate file type
    const isImage = /image/.test(file.type);
    const isVideo = /video/.test(file.type);
    if ((mediaType === 'image' && !isImage) || (mediaType === 'video' && !isVideo)) {
      setMediaErrors((prev) => ({
        ...prev,
        [mediaType]: intl.formatMessage(
          {
            id: 'invalidFileType',
            defaultMessage: 'Invalid file type. Please select a {mediaType}.',
          },
          { mediaType }
        ),
      }));
      return;
    }

    // Set preview from base64
    const base64Data = files.base64 || '';
    if (mediaType === 'image') {
      revokePreviewUrl(imagePreviewUrl);
      setImagePreviewUrl(base64Data);
    } else {
      revokePreviewUrl(videoPreviewUrl);
      setVideoPreviewUrl(base64Data);
    }

    // Convert base64 to File for upload
    const base64String = files.base64?.includes(',')
      ? files.base64.split(',')[1]
      : files.base64 || '';
    let blob: Blob | null = null;
    try {
      const byteChars = atob(base64String);
      const byteNumbers = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) {
        byteNumbers[i] = byteChars.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      blob = new Blob([byteArray], { type: file.type || 'application/octet-stream' });
    } catch (e) {
      setMediaErrors((prev) => ({
        ...prev,
        [mediaType]: intl.formatMessage({
          id: 'failedToProcessFile',
          defaultMessage: 'Failed to process file.',
        }),
      }));
      return;
    }

    let fileToUpload: File | Blob = blob;
    try {
      fileToUpload = new File([blob], file.name || 'upload', { type: file.type });
    } catch (e) {
      (blob as any).name = file.name || 'upload';
      fileToUpload = blob;
    }

    const formData = createMediaFormData(fileToUpload as File);
    const metadata = await uploadMediaFile(formData, mediaType);
    if (metadata) {
      if (mediaType === 'image') {
        setImageMedia(metadata);
        setLegacyImagePreview('');
      } else {
        setVideoMedia(metadata);
        setLegacyVideoPreview('');
      }
    } else {
      if (mediaType === 'image') {
        setImagePreviewUrl('');
      } else {
        setVideoPreviewUrl('');
      }
    }
  };

  const handleImageFiles = (files: any) => handleFiles(files, 'image');
  const handleVideoFiles = (files: any) => handleFiles(files, 'video');

  const clearImage = () => {
    setImageMedia(null);
    revokePreviewUrl(imagePreviewUrl);
    setImagePreviewUrl('');
    setLegacyImagePreview('');
    setMediaErrors((prev) => ({ ...prev, image: '' }));
  };

  const clearVideo = () => {
    setVideoMedia(null);
    revokePreviewUrl(videoPreviewUrl);
    setVideoPreviewUrl('');
    setLegacyVideoPreview('');
    setMediaErrors((prev) => ({ ...prev, video: '' }));
  };

  return (
    <ModalDialog
      title={
        isEditing
          ? intl.formatMessage({ id: 'editUpdate', defaultMessage: 'Edit Update' })
          : intl.formatMessage({ id: 'shareUpdate', defaultMessage: 'Share Update' })
      }
      isOpen={isOpen}
      close={closeModal}
      onReady={handleSubmit}
      disabled={!canSubmit || isUploadingMedia}
      submitButtonText={
        isEditing
          ? intl.formatMessage({ id: 'save', defaultMessage: 'Save' })
          : intl.formatMessage({ id: 'share', defaultMessage: 'Share' })
      }
    >
      <div className="form-group">
        <label>{intl.formatMessage({ id: 'audience', defaultMessage: 'Audience' })}</label>
        <div className="d-flex" style={{ gap: '16px' }}>
          <label className="d-flex align-items-center" style={{ gap: '6px' }}>
            <input
              type="radio"
              name="whats-new-audience"
              value="all"
              checked={audience === 'all'}
              onChange={() => handleAudienceChange('all')}
            />
            {intl.formatMessage({ id: 'allSites', defaultMessage: 'All sites' })}
          </label>
          <label className="d-flex align-items-center" style={{ gap: '6px' }}>
            <input
              type="radio"
              name="whats-new-audience"
              value="specific"
              checked={audience === 'specific'}
              onChange={() => handleAudienceChange('specific')}
            />
            {intl.formatMessage({ id: 'chooseSites', defaultMessage: 'Choose sites' })}
          </label>
        </div>
      </div>

      {audience === 'specific' && (
        <div className="form-group">
          <label htmlFor="whats-new-site-select">
            {intl.formatMessage({ id: 'selectSites', defaultMessage: 'Select sites' })}
          </label>
          <Select
            inputId="whats-new-site-select"
            isMulti
            options={siteOptions}
            value={selectedSiteOptions}
            onChange={handleSiteSelectChange}
            className="custom-tx-select"
            classNamePrefix="custom-tx"
            placeholder={intl.formatMessage({
              id: 'searchSites',
              defaultMessage: 'Search sites...',
            })}
          />
        </div>
      )}

      <div className="form-group">
        <label htmlFor="whats-new-title">
          {intl.formatMessage({ id: 'title', defaultMessage: 'Title' })}
        </label>
        <input
          id="whats-new-title"
          type="text"
          className="form-control"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={intl.formatMessage({ id: 'updateTitle', defaultMessage: 'Update title' })}
        />
      </div>

      <div className="form-group">
        <label htmlFor="whats-new-description">
          {intl.formatMessage({ id: 'description', defaultMessage: 'Description' })}
        </label>
        <ControlledRichText
          id="whats-new-description"
          input={{
            value: description,
            onChange: (value: string) => setDescription(value || ''),
          }}
          meta={{ initial: initialDescription }}
          enableHeadings={false}
        />
      </div>

      <div className="form-group">
        <label>{intl.formatMessage({ id: 'image', defaultMessage: 'Image' })}</label>
        <div className="d-flex align-items-center" style={{ gap: '8px', flexWrap: 'wrap' }}>
          <div className="upload-section">
            <ReactFileReader
              base64
              multipleFiles={false}
              handleFiles={handleImageFiles}
              fileTypes="image/*"
            >
              <button
                type="button"
                className="file-reader file-reader-button upload-button"
                disabled={mediaUploading.image}
              >
                <span className="upload-plus">+</span>
                <span className="upload-text">
                  {intl.formatMessage({ id: 'upload' })} {intl.formatMessage({ id: 'image' })}
                </span>
              </button>
            </ReactFileReader>
          </div>
          {(imageMedia || legacyImagePreview || imagePreviewUrl) && (
            <button type="button" className="button small" onClick={clearImage}>
              {intl.formatMessage({ id: 'removeImage', defaultMessage: 'Remove image' })}
            </button>
          )}
        </div>
        {mediaUploading.image && (
          <small className="text-muted d-block mt-1">
            {intl.formatMessage({ id: 'uploadingImage', defaultMessage: 'Uploading image...' })}
          </small>
        )}
        {mediaErrors.image && (
          <small className="text-danger d-block mt-1">{mediaErrors.image}</small>
        )}
        {(imagePreviewUrl || imageMedia?.downloadUrl || imageMedia?.url || legacyImagePreview) && (
          <div className="mt-2">
            <img
              src={
                imagePreviewUrl || imageMedia?.downloadUrl || imageMedia?.url || legacyImagePreview
              }
              alt={intl.formatMessage({
                id: 'uploadedPreview',
                defaultMessage: 'Uploaded preview',
              })}
              style={{ maxWidth: '50%', borderRadius: 8 }}
            />
          </div>
        )}
      </div>

      <div className="form-group">
        <label>{intl.formatMessage({ id: 'video', defaultMessage: 'Video' })}</label>
        <div className="d-flex align-items-center" style={{ gap: '8px', flexWrap: 'wrap' }}>
          <div className="upload-section">
            <ReactFileReader
              base64
              multipleFiles={false}
              handleFiles={handleVideoFiles}
              fileTypes="video/*"
            >
              <button
                type="button"
                className="file-reader file-reader-button upload-button"
                disabled={mediaUploading.video}
              >
                <span className="upload-plus">+</span>
                <span className="upload-text">
                  {intl.formatMessage({ id: 'upload' })} {intl.formatMessage({ id: 'video' })}
                </span>
              </button>
            </ReactFileReader>
          </div>
          {(videoMedia || legacyVideoPreview || videoPreviewUrl) && (
            <button type="button" className="button small" onClick={clearVideo}>
              {intl.formatMessage({ id: 'removeVideo', defaultMessage: 'Remove video' })}
            </button>
          )}
        </div>
        {mediaUploading.video && (
          <small className="text-muted d-block mt-1">
            {intl.formatMessage({ id: 'uploadingVideo', defaultMessage: 'Uploading video...' })}
          </small>
        )}
        {mediaErrors.video && (
          <small className="text-danger d-block mt-1">{mediaErrors.video}</small>
        )}
        {(videoPreviewUrl || videoMedia?.downloadUrl || videoMedia?.url || legacyVideoPreview) && (
          <div className="mt-2">
            <video
              controls
              src={
                videoPreviewUrl || videoMedia?.downloadUrl || videoMedia?.url || legacyVideoPreview
              }
              style={{ width: '100%', borderRadius: 8 }}
              preload="metadata"
            />
          </div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="whats-new-link-url">
          {intl.formatMessage({ id: 'linkUrl', defaultMessage: 'Link URL' })}
        </label>
        <input
          id="whats-new-link-url"
          type="text"
          className="form-control"
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          placeholder={intl.formatMessage({
            id: 'linkUrlPlaceholder',
            defaultMessage: 'https://example.com',
          })}
        />
      </div>

      <div className="form-group d-flex align-items-center" style={{ gap: '8px' }}>
        <input
          id="whats-new-require-login"
          type="checkbox"
          checked={requireLoginRead}
          onChange={(e) => setRequireLoginRead(e.target.checked)}
        />
        <label htmlFor="whats-new-require-login" style={{ marginBottom: 0 }}>
          {intl.formatMessage({
            id: 'requireUsersToReadOnLogin',
            defaultMessage: 'Require users to read on login',
          })}
        </label>
      </div>
    </ModalDialog>
  );
};

export default WhatsNewUpdateDialog;
